import { effect, inject, Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GoogleTranslateService } from 'src/app/services/google-translate.service';
import { DataChatService } from './dataChat.service';

const SILENT_WAV_DATA_URI = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

const FILLER: { listening: string[]; thinking: string[]; welcome: string[] } = {
    listening: ['Ok', 'vale', 'ummmm'],
    thinking: ['Vale, entendido... déjame que piense un poco...', 'Interesante.... déjame que lo vea por si puedo ayudarte.', 'Perfecto... dame un segundo que mire para ser mas exactos', 'Lo entiendo, dame un momento que busque bien la información y te digo'],
    welcome: ['Hola, seré tu asistente hoy... cuéntame, te escucho', 'Hola. ¿qué tal?, seré tu asistente de turismo']
};

@Injectable({
    providedIn: 'root'
})
export class LocalFillerPhrasesService {
    private dataChat = inject(DataChatService);
    private googleTranslate = inject(GoogleTranslateService);
    private lastIndex = -1;
    private sharedAudio: HTMLAudioElement | null = null;
    private audioUnlocked = false;
    private unlockInFlight = false;

    public isPlayingWelcome = signal(false);
    private welcomeAudio;

    listenAbortEffect = effect((onCleanup) => {
        const controller = this.dataChat.abortSignal();
        const onAbort = () => {
            this.isPlayingWelcome.set(false);
            try { 
                this.sharedAudio?.pause(); 
                this.welcomeAudio?.pause() 
            } catch {}
        };

        controller.signal.addEventListener('abort', onAbort);
        onCleanup(() => {
            controller.signal.removeEventListener('abort', onAbort);
        });
    });

    private getPhrases(type: 'listening' | 'thinking' | 'welcome'): string[] {
        console.log(`🗣️ [LocalFiller] Using Spanish filler phrase with user language=${this.getUserLanguage()}`);
        return FILLER[type];
    }

    public sayListeningFiller() {
        this.playFiller(this.getPhrases('listening'), 'listening');
    }

    public unlockAudioFromGesture(): void {
        if (this.audioUnlocked || this.unlockInFlight)
            return;

        this.unlockInFlight = true;
        const audio = this.getSharedAudio();
        audio.muted = true;
        audio.volume = 0;
        audio.src = SILENT_WAV_DATA_URI;

        const unlockPromise = audio.play();
        if (!unlockPromise || typeof unlockPromise.then !== 'function') {
            this.unlockInFlight = false;
            audio.muted = false;
            audio.volume = 1;
            return;
        }

        unlockPromise
            .then(() => {
                this.audioUnlocked = true;
            })
            .catch((err) => {
                console.warn('[LocalFiller] iOS audio unlock blocked:', err);
            })
            .finally(() => {
                try {
                    audio.pause();
                    audio.currentTime = 0;
                } catch {}
                audio.muted = false;
                audio.volume = 1;
                this.unlockInFlight = false;
            });
    }

    public playTypingSound() {
        const audio = this.getSharedAudio();
        this.resetAudioState(audio);
        audio.src = `${environment.apiAssistantUrlBase}/static/typing_sound.mp3?t=${Date.now()}`;
        audio.volume = 0.4;
        audio.play().catch(e => console.warn('⚠️ [LocalFiller] SFX typing error:', e));
    }

    public playWelcome(): Promise<void> {
        const welcome = this.getPhrases('welcome');
        if (!welcome?.length) 
            return Promise.resolve();

        this.isPlayingWelcome.set(true);
        let index: number;
        do { index = Math.floor(Math.random() * welcome.length);
        } while (index === this.lastIndex && welcome.length > 1);

        this.lastIndex = index;
        const phrase = welcome[index];

        const userLanguage = this.getUserLanguage();
        const url =
            `${environment.apiAssistantUrlBase}/tts/simple` +
            `?text=${encodeURIComponent(phrase)}` +
            `&language=${encodeURIComponent(userLanguage)}` +
            `&t=${Date.now()}`;

        const audio = new Audio(url);
        this.welcomeAudio = audio;

        return new Promise<void>((resolve, reject) => {
            const cleanup = () => {
                audio?.pause();
                audio.removeEventListener("ended", onEnded);
                audio.removeEventListener("error", onError);
                audio.removeEventListener("abort", onError);
                audio.removeEventListener("stalled", onError);
                audio.removeEventListener("emptied", onError);
            };

            const finishOk = (error?) => {
                this.isPlayingWelcome.set(false);
                if (error)
                    console.error("Audio Welcome Error: ", error);
                
                cleanup();
                resolve();
            };

            const finishKo = (error?) => {
                this.isPlayingWelcome.set(false);
                if (error)
                    console.error("Audio Welcome Error: ", error);
                
                cleanup();
                reject();
            };

            const onEnded = () => finishOk();
            const onError = () => finishKo(new Error("Audio error / cannot be played"));

            audio.addEventListener("ended", onEnded, { once: true });
            audio.addEventListener("error", onError, { once: true });
            audio.addEventListener("abort", onError, { once: true });

            audio.play()
                .then(() => {
                    console.log(`🗣️ [LocalFiller] Playing (welcome): ${phrase}`);
                })
                .catch((err) => {
                    console.warn("⚠️ [LocalFiller] Error playing audio filler:", err);
                    finishKo(err);
                });
        });
    }

    private playFiller(phrases: string[], type: 'listening' | 'thinking' | 'welcome') {
        if (!phrases?.length) 
            return;

        let index;
        do {
            index = Math.floor(Math.random() * phrases.length);
        } while (index === this.lastIndex && phrases.length > 1);

        this.lastIndex = index;
        const phrase = phrases[index];

        const userLanguage = this.getUserLanguage();
        const url = `${environment.apiAssistantUrlBase}/tts/simple?text=${encodeURIComponent(phrase)}&language=${encodeURIComponent(userLanguage)}&t=${Date.now()}`;

        const audio = this.getSharedAudio();
        this.resetAudioState(audio);
        audio.src = url;
        audio.volume = 1;

        audio.play().catch(err => {
            console.warn('⚠️ [LocalFiller] Error playing audio filler:', err);
        });

        if (type === 'thinking') {
            audio.onended = () => {
                audio.src = `${environment.apiAssistantUrlBase}/static/typing_sound.mp3?t=${Date.now()}`;
                audio.volume = 0.4;
                audio.play().catch(e => console.warn('⚠️ [LocalFiller] SFX error:', e));
            };
        }

        console.log(`🗣️ [LocalFiller] Playing (${type}): ${phrase}`);
    }

    private getUserLanguage(): string {
        const { value: cookieLang, source } = this.getLanguageFromCookie();
        const rawLang = cookieLang || this.getBrowserLanguage();
        const resolvedLang = this.normalizeLang(rawLang);
        console.log(`🗣️ [LocalFiller] Resolved user language: ${resolvedLang} (raw=${rawLang}, source=${source || 'browser'})`);
        return resolvedLang;
    }

    private normalizeLang(lang: string): string {
        let decoded = lang;
        try {
            decoded = decodeURIComponent(lang);
        } catch {
            decoded = lang;
        }
        decoded = decoded.trim().replace(/^"+|"+$/g, '');
        const fromPath = decoded.includes('/') ? decoded.split('/').filter(Boolean).pop() || decoded : decoded;
        const normalized = fromPath.toLowerCase().split(/[-_]/)[0];
        return normalized || 'es';
    }

    private getBrowserLanguage(): string {
        if (typeof navigator === 'undefined') 
            return 'es';

        const rawLang = navigator.languages?.[0] || navigator.language || 'es';
        return rawLang.toLowerCase();
    }

    private getLanguageFromCookie(): { value: string | null; source: string | null } {
        const localLang = this.googleTranslate.getLocalLang();
        if (localLang)
            return { value: localLang, source: 'locallang' };

        const googtransLang = this.googleTranslate.getCookieLang();
        if (googtransLang)
            return { value: googtransLang, source: 'googtrans' };

        const cookies = this.readCookies();
        const directCookieLang = cookies['lang']
            || cookies['language']
            || cookies['locale']
            || cookies['selectedLanguage']
            || cookies['i18next']
            || cookies['NEXT_LOCALE'];

        if (directCookieLang)
            return { value: directCookieLang, source: 'generic-cookie' };

        return { value: null, source: null };
    }

    private readCookies(): Record<string, string> {
        if (typeof document === 'undefined')
            return {};

        return document.cookie
            .split(';')
            .map(chunk => chunk.trim())
            .filter(Boolean)
            .reduce((acc, chunk) => {
                const separatorIndex = chunk.indexOf('=');
                if (separatorIndex < 0)
                    return acc;

                const key = chunk.slice(0, separatorIndex).trim();
                const value = chunk.slice(separatorIndex + 1).trim();
                try {
                    acc[key] = decodeURIComponent(value);
                } catch {
                    acc[key] = value;
                }
                return acc;
            }, {} as Record<string, string>);
    }

    private getSharedAudio(): HTMLAudioElement {
        if (!this.sharedAudio) {
            this.sharedAudio = new Audio();
            this.sharedAudio.preload = 'auto';
            this.sharedAudio.setAttribute('playsinline', 'true');
            this.sharedAudio.setAttribute('webkit-playsinline', 'true');
        }
        return this.sharedAudio;
    }

    private resetAudioState(audio: HTMLAudioElement): void {
        try { audio.pause(); } catch {}
        audio.onended = null;
        audio.onerror = null;
        try { audio.currentTime = 0; } catch {}
    }

}
